import { readTextEntities, type TextEntity } from '@andresot/protocol';
import { messageSegments } from './messageText';

function merge(entities: TextEntity[]): TextEntity[] {
	const result: TextEntity[] = [];
	for (const type of ['bold', 'italic'] as const) {
		for (const entity of entities
			.filter((item) => item.type === type && item.length > 0)
			.sort((a, b) => a.offset - b.offset)) {
			const last = result.at(-1);
			if (last?.type === type && last.offset + last.length >= entity.offset)
				last.length =
					Math.max(last.offset + last.length, entity.offset + entity.length) - last.offset;
			else result.push({ ...entity });
		}
	}
	return result;
}

export function toggleFormat(
	entities: TextEntity[],
	type: TextEntity['type'],
	start: number,
	end: number
): TextEntity[] {
	if (end <= start) return entities;
	const covered = entities.some(
		(item) => item.type === type && item.offset <= start && item.offset + item.length >= end
	);
	if (!covered) return merge([...entities, { type, offset: start, length: end - start }]);
	return merge(
		entities.flatMap((item) => {
			const stop = item.offset + item.length;
			if (item.type !== type || stop <= start || item.offset >= end) return [item];
			return [
				...(item.offset < start ? [{ ...item, length: start - item.offset }] : []),
				...(stop > end ? [{ ...item, offset: end, length: stop - end }] : [])
			];
		})
	);
}

/** Сдвиг интервалов при вводе, вставке, вырезании и undo обычного textarea. */
export function updateFormattedText(
	before: string,
	after: string,
	entities: TextEntity[]
): TextEntity[] {
	let start = 0;
	while (start < before.length && start < after.length && before[start] === after[start]) start++;
	let oldEnd = before.length,
		newEnd = after.length;
	while (oldEnd > start && newEnd > start && before[oldEnd - 1] === after[newEnd - 1]) {
		oldEnd--;
		newEnd--;
	}
	const delta = newEnd - oldEnd;
	const next = entities.flatMap((item) => {
		const stop = item.offset + item.length;
		if (stop <= start) return [item];
		if (item.offset >= oldEnd) return [{ ...item, offset: item.offset + delta }];
		if (item.offset <= start && stop >= oldEnd) return [{ ...item, length: item.length + delta }];
		return [
			...(item.offset < start ? [{ ...item, length: start - item.offset }] : []),
			...(stop > oldEnd ? [{ ...item, offset: newEnd, length: stop - oldEnd }] : [])
		];
	});
	return readTextEntities(after, merge(next));
}

export function addList(
	text: string,
	entities: TextEntity[],
	start: number,
	end: number,
	ordered: boolean
) {
	const lineStart = start === 0 ? 0 : text.lastIndexOf('\n', start - 1) + 1;
	const nextBreak = text.indexOf('\n', end > start && text[end - 1] === '\n' ? end - 1 : end);
	const lineEnd = nextBreak < 0 ? text.length : nextBreak;
	const lines = text.slice(lineStart, lineEnd).split('\n');
	const edits: Array<{ at: number; remove: number; insert: string }> = [];
	let cursor = lineStart;
	lines.forEach((line, index) => {
		const old = /^(?:• |\d+\. )/u.exec(line)?.[0] ?? '';
		edits.push({ at: cursor, remove: old.length, insert: ordered ? `${index + 1}. ` : '• ' });
		cursor += line.length + 1;
	});
	for (const edit of edits.reverse()) {
		const after = text.slice(0, edit.at) + edit.insert + text.slice(edit.at + edit.remove);
		entities = updateFormattedText(text, after, entities);
		text = after;
	}
	return { text, entities };
}

export function formattedSegments(
	text: string,
	entities: TextEntity[],
	mentionOf?: (login: string) => boolean
) {
	const valid = readTextEntities(text, entities);
	let offset = 0;
	return messageSegments(text, { mentionOf }).flatMap((segment) => {
		const start = offset,
			end = start + segment.text.length;
		offset = end;
		const bounds = [
			...new Set([
				start,
				end,
				...valid
					.flatMap((item) => [item.offset, item.offset + item.length])
					.filter((point) => point > start && point < end)
			])
		].sort((a, b) => a - b);
		return bounds.slice(0, -1).map((point, index) => ({
			...segment,
			text: text.slice(point, bounds[index + 1]),
			bold: valid.some(
				(item) =>
					item.type === 'bold' &&
					item.offset <= point &&
					item.offset + item.length >= bounds[index + 1]!
			),
			italic: valid.some(
				(item) =>
					item.type === 'italic' &&
					item.offset <= point &&
					item.offset + item.length >= bounds[index + 1]!
			)
		}));
	});
}
